import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

const TermsConditions = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-sm z-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-black cursor-pointer" onClick={() => navigate('/')}>Koru Talent</h1>
            </div>
            <Button 
              variant="ghost"
              onClick={() => navigate('/')}
              className="text-burgundy-800 hover:text-burgundy-900"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Button>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold text-black mb-4">Terms & Conditions</h1>
          <p className="text-gray-600 mb-8">Last updated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>

          <div className="space-y-8 text-gray-700 leading-relaxed">
            <section>
              <h2 className="text-2xl font-bold text-black mb-4">1. Introduction</h2>
              <p>
                These Terms and Conditions ("Terms") govern your use of the Koru Talent website and recruitment services. 
                By accessing our website or using our services, you agree to be bound by these Terms.
              </p>
              <p className="mt-4">
                Please read these Terms carefully before using our services. If you do not agree with any part of these Terms, 
                you should not use our website or services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-black mb-4">2. Definitions</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>"We", "us", "our"</strong> refers to Koru Talent</li>
                <li><strong>"Candidate"</strong> refers to individuals seeking employment through our services</li>
                <li><strong>"Client"</strong> refers to employers and businesses using our recruitment services</li>
                <li><strong>"Services"</strong> refers to all recruitment and career consulting services provided by Koru Talent</li>
                <li><strong>"Website"</strong> refers to korutalent.co.uk and all associated pages</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-black mb-4">3. Services Provided</h2>
              <h3 className="text-xl font-semibold text-burgundy-800 mb-3">3.1 Recruitment Services</h3>
              <p>We provide professional recruitment services including:</p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>Candidate sourcing and screening</li>
                <li>Job matching and placement</li>
                <li>Career consulting and guidance</li>
                <li>Interview coordination</li>
                <li>Employment advice</li>
              </ul>

              <h3 className="text-xl font-semibold text-burgundy-800 mb-3 mt-6">3.2 No Guarantee</h3>
              <p>
                Whilst we strive to provide the best possible service, we cannot guarantee job placement for candidates or 
                successful hires for clients. Employment decisions remain at the sole discretion of the employer.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-black mb-4">4. Terms for Candidates</h2>
              <h3 className="text-xl font-semibold text-burgundy-800 mb-3">4.1 Registration</h3>
              <p>
                By registering with Koru Talent, you confirm that all information provided is accurate, complete, and up-to-date. 
                You agree to notify us immediately of any changes to your information.
              </p>

              <h3 className="text-xl font-semibold text-burgundy-800 mb-3 mt-6">4.2 Candidate Obligations</h3>
              <p>As a candidate, you agree to:</p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>Provide truthful and accurate information in your CV and applications</li>
                <li>Inform us of any offers of employment you receive</li>
                <li>Attend scheduled interviews and meetings punctually</li>
                <li>Notify us immediately if you wish to withdraw from the recruitment process</li>
                <li>Not apply directly to our clients for positions we have introduced to you</li>
                <li>Provide all necessary documentation for right to work verification</li>
              </ul>

              <h3 className="text-xl font-semibold text-burgundy-800 mb-3 mt-6">4.3 Fees</h3>
              <p>
                Our services are free for candidates. We do not charge candidates any fees for job placement or career consulting services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-black mb-4">5. Terms for Clients (Employers)</h2>
              <h3 className="text-xl font-semibold text-burgundy-800 mb-3">5.1 Engagement</h3>
              <p>
                By engaging Koru Talent's services, you agree to provide accurate job descriptions, requirements, and company information. 
                You agree to conduct fair and lawful recruitment processes.
              </p>

              <h3 className="text-xl font-semibold text-burgundy-800 mb-3 mt-6">5.2 Client Obligations</h3>
              <p>As a client, you agree to:</p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>Provide timely feedback on candidates presented</li>
                <li>Conduct interviews in a professional and timely manner</li>
                <li>Notify us of any offers made to our candidates</li>
                <li>Not approach candidates directly for other positions without our consent</li>
                <li>Comply with all employment laws and regulations</li>
              </ul>

              <h3 className="text-xl font-semibold text-burgundy-800 mb-3 mt-6">5.3 Fees and Payment</h3>
              <p>
                Our fees for recruitment services will be agreed upon separately with each client. Payment terms and conditions 
                will be outlined in individual service agreements. Unless otherwise agreed, our standard terms are:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>Payment due within 30 days of invoice date</li>
                <li>Late payment may incur interest charges</li>
                <li>Refund policies as per individual agreements</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-black mb-4">6. Intellectual Property</h2>
              <p>
                All content on our website, including text, graphics, logos, images, and software, is the property of Koru Talent 
                or its content suppliers and is protected by UK and international copyright laws.
              </p>
              <p className="mt-4">
                You may not reproduce, distribute, modify, or create derivative works from our content without our express written permission.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-black mb-4">7. Confidentiality</h2>
              <p>
                We treat all information provided by candidates and clients as confidential. You agree to keep confidential any 
                information shared during the recruitment process, including:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>Candidate details (for clients)</li>
                <li>Client information and job details (for candidates)</li>
                <li>Terms of engagement and fees</li>
                <li>Any proprietary or sensitive information</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-black mb-4">8. Data Protection</h2>
              <p>
                We are committed to protecting your personal data in accordance with UK GDPR and the Data Protection Act 2018. 
                Please refer to our <span className="text-burgundy-800 font-semibold cursor-pointer hover:underline" onClick={() => navigate('/privacy-policy')}>Privacy Policy</span> for 
                detailed information on how we collect, use, and protect your data.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-black mb-4">9. Limitation of Liability</h2>
              <h3 className="text-xl font-semibold text-burgundy-800 mb-3">9.1 Service Limitations</h3>
              <p>
                We provide our services with reasonable care and skill. However, we do not guarantee:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>Successful job placement for candidates</li>
                <li>Suitability of candidates for specific roles</li>
                <li>Accuracy of all information provided by third parties</li>
                <li>Uninterrupted access to our website</li>
              </ul>

              <h3 className="text-xl font-semibold text-burgundy-800 mb-3 mt-6">9.2 Exclusions</h3>
              <p>
                To the fullest extent permitted by law, we exclude all liability for:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>Indirect or consequential losses</li>
                <li>Loss of profits, revenue, or business opportunities</li>
                <li>Actions or omissions of candidates or clients</li>
                <li>Decisions made based on our advice or recommendations</li>
              </ul>
              <p className="mt-4">
                Nothing in these Terms excludes our liability for death or personal injury caused by our negligence, fraud, or 
                any other liability that cannot be excluded by law.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-black mb-4">10. Website Use</h2>
              <h3 className="text-xl font-semibold text-burgundy-800 mb-3">10.1 Acceptable Use</h3>
              <p>You agree not to:</p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>Use the website for any unlawful purpose</li>
                <li>Attempt to gain unauthorised access to our systems</li>
                <li>Transmit any viruses or harmful code</li>
                <li>Interfere with other users' access to the website</li>
                <li>Collect data from the website using automated means</li>
                <li>Impersonate any person or entity</li>
              </ul>

              <h3 className="text-xl font-semibold text-burgundy-800 mb-3 mt-6">10.2 Website Availability</h3>
              <p>
                We aim to ensure our website is available 24/7, but we do not guarantee uninterrupted access. We may suspend or 
                restrict access for maintenance, upgrades, or other operational reasons.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-black mb-4">11. Third-Party Links</h2>
              <p>
                Our website may contain links to third-party websites. We are not responsible for the content, privacy policies, 
                or practices of these websites. Access to third-party sites is at your own risk.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-black mb-4">12. Termination</h2>
              <p>
                We reserve the right to terminate or suspend your access to our services at any time, with or without notice, 
                if you breach these Terms or engage in conduct that we deem inappropriate or harmful.
              </p>
              <p className="mt-4">
                You may terminate your relationship with us at any time by contacting us and requesting removal from our database.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-black mb-4">13. Dispute Resolution</h2>
              <p>
                We encourage you to contact us first if you have any concerns or disputes. We will make reasonable efforts to 
                resolve issues amicably.
              </p>
              <p className="mt-4">
                If a dispute cannot be resolved through direct communication, you agree to attempt mediation before pursuing 
                legal action.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-black mb-4">14. Governing Law</h2>
              <p>
                These Terms are governed by and construed in accordance with the laws of England and Wales. Any disputes arising 
                from these Terms or our services shall be subject to the exclusive jurisdiction of the courts of England and Wales.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-black mb-4">15. Changes to Terms</h2>
              <p>
                We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting on this page. 
                Your continued use of our services after changes are posted constitutes acceptance of the modified Terms.
              </p>
              <p className="mt-4">
                We recommend reviewing these Terms periodically to stay informed of any updates.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-black mb-4">16. Severability</h2>
              <p>
                If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions shall continue 
                in full force and effect.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-black mb-4">17. Entire Agreement</h2>
              <p>
                These Terms, together with our Privacy Policy, constitute the entire agreement between you and Koru Talent regarding 
                the use of our website and services, superseding any prior agreements.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-black mb-4" data-testid="terms-email-heading">18. Email Us</h2>
              <p>
                If you have any questions about these Terms and Conditions, please email us:
              </p>
              <div className="mt-4 p-6 bg-gray-50 rounded-lg border border-gray-200">
                <p className="font-semibold text-black">Koru Talent</p>
                <p className="mt-2">Email: <a href="mailto:christian@korutalent.co.uk" className="text-burgundy-800 hover:text-burgundy-900 underline">christian@korutalent.co.uk</a></p>
              </div>
            </section>

            <section className="bg-burgundy-50 p-6 rounded-lg border-2 border-burgundy-200">
              <h2 className="text-xl font-bold text-burgundy-900 mb-3">Acknowledgement</h2>
              <p className="text-burgundy-800">
                By using our website and services, you acknowledge that you have read, understood, and agree to be bound by these 
                Terms and Conditions.
              </p>
            </section>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-black text-white py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center text-gray-400">
          <p>&copy; {new Date().getFullYear()} Koru Talent. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default TermsConditions;